import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import Primary from "../pages/primary/page";
import Middle from "../pages/middle/page";
import High from "../pages/high/page";
import MagicDrawing from "../pages/primary/magic-drawing/page";
import SmartCompanion from "../pages/primary/smart-companion/page";
import ImageRecognition from "../pages/primary/image-recognition/page";
import VoiceAssistant from "../pages/primary/voice-assistant/page";
import AlgorithmGames from "../pages/primary/algorithm-games/page";
import CreativeWorkshop from "../pages/primary/creative-workshop/page";
import { lazy } from "react";

const PrimaryVoiceAssistantPage = lazy(() => import('../pages/primary/voice-assistant/page'));
const PrimaryAlgorithmGamesPage = lazy(() => import('../pages/primary/algorithm-games/page'));
const PrimaryCreativeWorkshopPage = lazy(() => import('../pages/primary/creative-workshop/page'));
const PrimaryCodingGamePage = lazy(() => import('../pages/primary/coding-game/page'));
const PrimaryComputationalThinkingPage = lazy(() => import('../pages/primary/computational-thinking/page'));
const PrimaryAIRecognitionPage = lazy(() => import('../pages/primary/ai-recognition/page'));
const PrimaryVisualProgrammingPage = lazy(() => import('../pages/primary/visual-programming/page'));
const PrimaryStoryCreatorPage = lazy(() => import('../pages/primary/story-creator/page'));

// Primary Grade Pages
const PrimaryGrade1Page = lazy(() => import('../pages/primary/grade1/page'));
const PrimaryGrade2Page = lazy(() => import('../pages/primary/grade2/page'));
const PrimaryGrade3Page = lazy(() => import('../pages/primary/grade3/page'));
const PrimaryGrade4Page = lazy(() => import('../pages/primary/grade4/page'));
const PrimaryGrade5Page = lazy(() => import('../pages/primary/grade5/page'));
const PrimaryGrade6Page = lazy(() => import('../pages/primary/grade6/page'));

// Middle School Module Pages
const MiddleNeuralNetworkPage = lazy(() => import('../pages/middle/neural-network/page'));
const MiddleAgentBuilderPage = lazy(() => import('../pages/middle/agent-builder/page'));
const MiddleMLPlaygroundPage = lazy(() => import('../pages/middle/ml-playground/page'));
const MiddleIoTSimulatorPage = lazy(() => import('../pages/middle/iot-simulator/page'));
const MiddleInterdisciplinaryProjectPage = lazy(() => import('../pages/middle/interdisciplinary-project/page'));
const MiddlePromptLabPage = lazy(() => import('../pages/middle/prompt-lab/page'));
const MiddleVisualProgrammingPage = lazy(() => import('../pages/middle/visual-programming/page'));

// High School Module Pages
const HighLLMTuningPage = lazy(() => import('../pages/high/llm-tuning/page'));
const HighPythonIDEPage = lazy(() => import('../pages/high/python-ide/page'));
const HighModelTrainingPage = lazy(() => import('../pages/high/model-training/page'));
const HighEthicsDebatePage = lazy(() => import('../pages/high/ethics-debate/page'));
const HighResearchIncubatorPage = lazy(() => import('../pages/high/research-incubator/page'));
const HighIndustryCasePage = lazy(() => import('../pages/high/industry-case/page'));

// Feature Pages
const CurriculumSystemPage = lazy(() => import('../pages/features/curriculum-system/page'));
const AgentBuilderFeaturePage = lazy(() => import('../pages/features/agent-builder/page'));
const NeuralNetworkFeaturePage = lazy(() => import('../pages/features/neural-network/page'));
const SecurityPage = lazy(() => import('../pages/features/security/page'));
const AssessmentPage = lazy(() => import('../pages/features/assessment/page'));
const TeacherEmpowermentPage = lazy(() => import('../pages/features/teacher-empowerment/page'));

// Neural Network Detail Pages
const FeedforwardNetworkPage = lazy(() => import('../pages/features/neural-network/feedforward/page'));
const CNNPage = lazy(() => import('../pages/features/neural-network/cnn/page'));
const RNNPage = lazy(() => import('../pages/features/neural-network/rnn/page'));
const TransformerPage = lazy(() => import('../pages/features/neural-network/transformer/page'));

// Teacher and Admin Pages
const TeacherPage = lazy(() => import('../pages/teacher/page'));
const AdminPage = lazy(() => import('../pages/admin/page'));

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/primary",
    element: <Primary />,
  },
  {
    path: "/primary/magic-drawing",
    element: <MagicDrawing />,
  },
  {
    path: "/primary/smart-companion",
    element: <SmartCompanion />,
  },
  {
    path: "/primary/image-recognition",
    element: <ImageRecognition />,
  },
  {
    path: "/primary/voice-assistant",
    element: <VoiceAssistant />,
  },
  {
    path: "/primary/algorithm-games",
    element: <AlgorithmGames />,
  },
  {
    path: "/primary/creative-workshop",
    element: <PrimaryCreativeWorkshopPage />,
  },
  {
    path: "/primary/coding-game",
    element: <PrimaryCodingGamePage />,
  },
  {
    path: "/primary/computational-thinking",
    element: <PrimaryComputationalThinkingPage />,
  },
  {
    path: "/primary/ai-recognition",
    element: <PrimaryAIRecognitionPage />,
  },
  {
    path: "/primary/visual-programming",
    element: <PrimaryVisualProgrammingPage />,
  },
  {
    path: "/primary/story-creator",
    element: <PrimaryStoryCreatorPage />,
  },
  {
    path: "/primary/grade1",
    element: <PrimaryGrade1Page />,
  },
  {
    path: "/primary/grade2",
    element: <PrimaryGrade2Page />,
  },
  {
    path: "/primary/grade3",
    element: <PrimaryGrade3Page />,
  },
  {
    path: "/primary/grade4",
    element: <PrimaryGrade4Page />,
  },
  {
    path: "/primary/grade5",
    element: <PrimaryGrade5Page />,
  },
  {
    path: "/primary/grade6",
    element: <PrimaryGrade6Page />,
  },
  {
    path: "/middle",
    element: <Middle />,
  },
  {
    path: "/high",
    element: <High />,
  },
  {
    path: "/middle/neural-network",
    element: <MiddleNeuralNetworkPage />,
  },
  {
    path: "/middle/agent-builder",
    element: <MiddleAgentBuilderPage />,
  },
  {
    path: "/middle/ml-playground",
    element: <MiddleMLPlaygroundPage />,
  },
  {
    path: "/middle/iot-simulator",
    element: <MiddleIoTSimulatorPage />,
  },
  {
    path: "/middle/interdisciplinary-project",
    element: <MiddleInterdisciplinaryProjectPage />,
  },
  {
    path: "/middle/prompt-lab",
    element: <MiddlePromptLabPage />,
  },
  {
    path: "/middle/visual-programming",
    element: <MiddleVisualProgrammingPage />,
  },
  {
    path: "/high/llm-tuning",
    element: <HighLLMTuningPage />,
  },
  {
    path: "/high/python-ide",
    element: <HighPythonIDEPage />,
  },
  {
    path: "/high/model-training",
    element: <HighModelTrainingPage />,
  },
  {
    path: "/high/ethics-debate",
    element: <HighEthicsDebatePage />,
  },
  {
    path: "/high/research-incubator",
    element: <HighResearchIncubatorPage />,
  },
  {
    path: "/high/industry-case",
    element: <HighIndustryCasePage />,
  },
  {
    path: '/teacher',
    element: <TeacherPage />,
  },
  {
    path: '/admin',
    element: <AdminPage />,
  },
  {
    path: '/features/curriculum-system',
    element: <CurriculumSystemPage />,
  },
  {
    path: '/features/agent-builder',
    element: <AgentBuilderFeaturePage />,
  },
  {
    path: '/features/neural-network',
    element: <NeuralNetworkFeaturePage />,
  },
  {
    path: '/features/neural-network/feedforward',
    element: <FeedforwardNetworkPage />,
  },
  {
    path: '/features/neural-network/cnn',
    element: <CNNPage />,
  },
  {
    path: '/features/neural-network/rnn',
    element: <RNNPage />,
  },
  {
    path: '/features/neural-network/transformer',
    element: <TransformerPage />,
  },
  {
    path: '/features/security',
    element: <SecurityPage />,
  },
  {
    path: '/features/assessment',
    element: <AssessmentPage />,
  },
  {
    path: '/features/teacher-empowerment',
    element: <TeacherEmpowermentPage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
